"use client";

import { useEffect, useMemo } from "react";
import posthog from "posthog-js";

import type { AgentCardAgent } from "./agent-card";
import type { AdAgentResultsPhase, AdAgentSort } from "@/lib/data/agents";

export const AGENT_RESULT_CARD_IMPRESSION_EVENT =
  "agent_result_card_impression";

interface AgentResultImpressionsProps {
  orgIdentifier: string;
  agents: AgentCardAgent[];
  phase: AdAgentResultsPhase;
  globalPage: number;
  sort: AdAgentSort;
  /** Canonical URL-state serialization for this results visit. */
  viewKey: string;
}

interface ImpressionVisit {
  key: string;
  generation: number;
}

let activeVisitKey: string | null = null;
let activeVisitGeneration = 0;
let emittedAgentIds = new Set<string>();

function enterVisit(key: string): ImpressionVisit {
  activeVisitGeneration += 1;
  if (activeVisitKey !== key) {
    activeVisitKey = key;
    emittedAgentIds = new Set();
  }
  return { key, generation: activeVisitGeneration };
}

function leaveVisit(visit: ImpressionVisit): void {
  // Strict Mode performs setup → cleanup → setup synchronously. Defer the
  // reset by one microtask so the second setup can renew the visit generation.
  // A real navigation has no renewal, so returning later records impressions.
  queueMicrotask(() => {
    if (
      activeVisitKey === visit.key &&
      activeVisitGeneration === visit.generation
    ) {
      activeVisitKey = null;
      emittedAgentIds = new Set();
    }
  });
}

function markAgentEmitted(visitKey: string, agentId: string): boolean {
  if (activeVisitKey !== visitKey || emittedAgentIds.has(agentId)) return false;
  emittedAgentIds.add(agentId);
  return true;
}

/**
 * Client-only PostHog leaf for the visible result cards. PostHog's anonymous
 * session id supplies the visitor-session denominator for the +2-week fairness
 * query. Event properties stay explicit and non-PII; the ordering seed never
 * crosses this component boundary. Renders no UI.
 */
export function AgentResultImpressions({
  orgIdentifier,
  agents,
  phase,
  globalPage,
  sort,
  viewKey,
}: AgentResultImpressionsProps) {
  const orderedAgentIds = useMemo(
    () => agents.map((agent) => agent.agentId),
    [agents],
  );
  const visitKey = `${orgIdentifier}|${viewKey}|${phase}`;

  useEffect(() => {
    const visit = enterVisit(visitKey);

    if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      orderedAgentIds.forEach((agentId, index) => {
        if (!markAgentEmitted(visit.key, agentId)) return;
        try {
          posthog.capture(AGENT_RESULT_CARD_IMPRESSION_EVENT, {
            org_id: orgIdentifier,
            agent_id: agentId,
            phase,
            global_page: globalPage,
            position: index + 1,
            sort,
          });
        } catch {
          // Telemetry must never break the results page.
        }
      });
    }

    return () => leaveVisit(visit);
  }, [globalPage, orderedAgentIds, orgIdentifier, phase, sort, visitKey]);

  return null;
}
